import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Lock, Smartphone } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import * as api from "@/services/api";

type Action = { kind: "phone" | "seat"; holder: api.GpsSeatHolder };

const fmt = (d?: string | null) =>
	d ? new Date(d).toLocaleDateString(undefined, { dateStyle: "medium" }) : "—";

/**
 * GPS seat holders of one organization, with the two releases only FlowMetrics
 * may make: a seat inside its 30-day lock, and an employee's bound phone. Both
 * need a reason, and both are written to the activity log on the server.
 */
export function GpsSeatHoldersCard({ orgId }: { orgId: string }) {
	const queryClient = useQueryClient();
	const [action, setAction] = useState<Action | null>(null);
	const [reason, setReason] = useState("");

	const { data, isLoading, isError } = useQuery({
		queryKey: ["admin-gps-seat-holders", orgId],
		queryFn: () => api.getGpsSeatHolders(orgId),
	});

	const release = useMutation({
		mutationFn: (a: Action) =>
			a.kind === "phone"
				? api.releaseMobileDevice(orgId, a.holder._id, reason.trim())
				: api.releaseGpsSeat(orgId, a.holder._id, reason.trim()),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["admin-gps-seat-holders", orgId],
			});
			queryClient.invalidateQueries({
				queryKey: ["admin-organization-attendance-seats", orgId],
			});
			setAction(null);
			setReason("");
		},
	});

	const now = Date.now();
	const holders = data?.holders ?? [];

	return (
		<Card className="lg:col-span-2">
			<CardHeader>
				<CardTitle className="flex items-center gap-2">
					<Lock className="w-5 h-5" />
					GPS Seat Holders
				</CardTitle>
			</CardHeader>
			<CardContent className="space-y-3">
				<p className="text-sm text-gray-600">
					A seat stays with its employee for 30 days after it is assigned, and
					each holder can use only their first phone. The organization cannot
					change either — release here only on a verified request.
				</p>

				{isLoading ? (
					<Loader2 className="w-5 h-5 animate-spin text-gray-400" />
				) : isError ? (
					<p className="text-sm text-red-600">Could not load seat holders.</p>
				) : holders.length === 0 ? (
					<p className="text-sm text-gray-500">No seat holders.</p>
				) : (
					<div className="overflow-x-auto">
						<table className="w-full text-sm">
							<thead>
								<tr className="text-left text-xs text-gray-500 border-b">
									<th className="py-2 pr-3 font-medium">Employee</th>
									<th className="py-2 pr-3 font-medium">Seat</th>
									<th className="py-2 pr-3 font-medium">Phone</th>
									<th className="py-2 font-medium text-right">Release</th>
								</tr>
							</thead>
							<tbody>
								{holders.map((h) => {
									const locked =
										!!h.seatLockedUntil &&
										new Date(h.seatLockedUntil).getTime() > now;
									const name =
										`${h.firstName ?? ""} ${h.lastName ?? ""}`.trim() ||
										h.email ||
										h._id;
									return (
										<tr key={h._id} className="border-b last:border-0">
											<td className="py-2 pr-3">
												<div className="font-medium text-gray-900">{name}</div>
												<div className="text-xs text-gray-500">{h.email}</div>
												{h.departed && (
													<Badge variant="secondary" className="mt-1">
														Left the organization
													</Badge>
												)}
											</td>
											<td className="py-2 pr-3 text-xs">
												{locked ? (
													<span className="text-amber-700">
														Locked until {fmt(h.seatLockedUntil)}
													</span>
												) : (
													<span className="text-gray-500">Unlocked</span>
												)}
											</td>
											<td className="py-2 pr-3 text-xs">
												{h.boundDevice ? (
													<>
														<div className="text-gray-900">
															{[h.boundDevice.deviceModel, h.boundDevice.platform]
																.filter(Boolean)
																.join(" · ") || "Registered phone"}
														</div>
														<div className="text-gray-500">
															Bound {fmt(h.boundDevice.boundAt)} · last seen{" "}
															{fmt(h.boundDevice.lastSeenAt)}
														</div>
													</>
												) : (
													<span className="text-gray-500">No phone yet</span>
												)}
											</td>
											<td className="py-2 text-right whitespace-nowrap">
												<Button
													size="sm"
													variant="outline"
													className="mr-2"
													disabled={!h.boundDevice}
													onClick={() => setAction({ kind: "phone", holder: h })}
												>
													<Smartphone className="w-3.5 h-3.5 mr-1" />
													Phone
												</Button>
												<Button
													size="sm"
													variant="outline"
													onClick={() => setAction({ kind: "seat", holder: h })}
												>
													Seat
												</Button>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
			</CardContent>

			<Dialog
				open={action !== null}
				onOpenChange={(o) => {
					if (!o) {
						setAction(null);
						setReason("");
						release.reset();
					}
				}}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>
							{action?.kind === "phone" ? "Release phone" : "Release seat"}
						</DialogTitle>
						<DialogDescription>
							{action?.kind === "phone"
								? "The employee's current phone stops working for GPS attendance. The next phone they log in on becomes their phone."
								: "The seat is taken from this employee now, even inside its 30-day lock, and their phone is released with it. The organization can then give the seat to someone else."}
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-1">
						<label className="block text-sm font-medium text-gray-700">
							Reason (who asked, and why)
						</label>
						<Input
							value={reason}
							onChange={(e) => setReason(e.target.value)}
							placeholder="e.g. Ticket #123 — phone lost, requested by HR"
						/>
						{release.isError && (
							<p className="text-xs text-red-600">
								{release.error instanceof Error
									? release.error.message
									: "Release failed"}
							</p>
						)}
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => {
								setAction(null);
								setReason("");
							}}
						>
							Cancel
						</Button>
						<Button
							variant="destructive"
							disabled={reason.trim().length < 3 || release.isPending}
							onClick={() => action && release.mutate(action)}
						>
							{release.isPending && (
								<Loader2 className="w-4 h-4 mr-1 animate-spin" />
							)}
							Release
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</Card>
	);
}
